// src/runtime/BrandLibraryRuntime/config.ts
// ============================================
// Brand Library 配置（Phase 4 Sprint 1 Task 3）
//
// 9 大品牌完整画像：
//   AI / 创业 / 量化 / 酒店 / 国学 / Agent / 企业管理 / 产品 / XEAOS
//
// 每个品牌包含：
//   1. 基础信息（标签/名称/描述/定位）
//   2. 关键词（用于内容分类）
//   3. 主推平台
//   4. 品牌调性（语气/风格/禁用词/推荐用词）
//   5. 视觉规范（主色/辅色/字体/图片风格）
//   6. 目标受众
//   7. 内容支柱（3-5 个内容方向）
// ============================================

import type { BrandProfile } from './types';

// ============================================
// 9 大品牌完整画像
// ============================================

export const BRAND_LIBRARY: BrandProfile[] = [
  // ============================================
  // 1. AI
  // ============================================
  {
    tag: 'AI',
    name: 'AI 技术',
    description: '人工智能技术与应用',
    positioning: '面向技术决策者与开发者，提供 AI 技术趋势、落地实践与深度思考',
    keywords: ['AI', '人工智能', '大模型', 'LLM', 'GPT', '机器学习', '深度学习', 'AIGC', '神经网络', 'Transformer'],
    primaryPlatforms: ['wechat', 'zhihu'],
    voice: {
      tone: '专业严谨',
      style: '数据驱动 + 技术深度',
      forbiddenWords: ['吹爆', '牛逼', '逆天'],
      signatureWords: ['范式', '涌现', '对齐', '可解释性', '落地'],
    },
    visual: {
      primaryColor: '#2563EB',
      secondaryColor: '#1E40AF',
      fontFamily: '无衬线',
      imageStyle: '蓝色科技感，神经网络节点图，数据可视化',
    },
    targetAudience: 'CTO、技术负责人、AI 工程师、产品经理',
    contentPillars: ['技术趋势', '落地案例', '原理拆解', '工具评测', '行业影响'],
  },

  // ============================================
  // 2. 创业
  // ============================================
  {
    tag: '创业',
    name: '创业笔记',
    description: '创业经验与商业思考',
    positioning: '面向创业者与早期团队，分享真实创业经验、商业模式与增长策略',
    keywords: ['创业', '商业模式', '融资', 'MVP', '增长', 'PMF', '冷启动', 'SaaS', 'B2B', ' ARR'],
    primaryPlatforms: ['wechat', 'xiaohongshu'],
    voice: {
      tone: '真诚坦率',
      style: '故事化 + 实战经验',
      forbiddenWords: ['稳赚', '暴富', '躺赚'],
      signatureWords: ['试错', '迭代', 'PMF', '单位经济', '复利'],
    },
    visual: {
      primaryColor: '#F59E0B',
      secondaryColor: '#D97706',
      fontFamily: '无衬线',
      imageStyle: '暖色调，手写笔记风格，创业场景插画',
    },
    targetAudience: '创业者、早期团队负责人、独立开发者',
    contentPillars: ['创业复盘', '商业模式', '融资经验', '团队管理', '增长黑客'],
  },

  // ============================================
  // 3. 量化
  // ============================================
  {
    tag: '量化',
    name: '量化交易',
    description: '量化交易与金融科技',
    positioning: '面向量化从业者与金融科技爱好者，分享策略、回测、因子与风控经验',
    keywords: ['量化', '交易', '策略', '回测', '因子', 'Alpha', '风控', '金融科技', 'CTA', '对冲'],
    primaryPlatforms: ['zhihu', 'wechat'],
    voice: {
      tone: '理性冷静',
      style: '数据密集 + 模型推导',
      forbiddenWords: ['必涨', '稳赢', '内幕'],
      signatureWords: ['夏普', '回撤', '胜率', '盈亏比', '因子暴露'],
    },
    visual: {
      primaryColor: '#059669',
      secondaryColor: '#047857',
      fontFamily: '等宽字体',
      imageStyle: '绿色涨跌图，K线图，因子热力图',
    },
    targetAudience: '量化研究员、交易员、金融科技从业者',
    contentPillars: ['策略研究', '因子挖掘', '回测分析', '风控体系', '市场观察'],
  },

  // ============================================
  // 4. 酒店
  // ============================================
  {
    tag: '酒店',
    name: '酒店数字化',
    description: '酒店行业数字化与运营',
    positioning: '面向酒店业主与运营管理者，提供数字化转型、收益管理与客户体验提升方案',
    keywords: ['酒店', 'OTA', '收益管理', 'RevPAR', '客户体验', '数字化转型', '酒店SaaS', 'PMS', '直销', '会员体系'],
    primaryPlatforms: ['xiaohongshu', 'wechat'],
    voice: {
      tone: '务实落地',
      style: '案例驱动 + 行业洞察',
      forbiddenWords: ['颠覆', '革命性'],
      signatureWords: ['RevPAR', '入住率', 'ADR', '直销占比', '会员复购'],
    },
    visual: {
      primaryColor: '#7C3AED',
      secondaryColor: '#5B21B6',
      fontFamily: '无衬线',
      imageStyle: '紫色高端感，酒店大堂/客房场景，数据仪表盘',
    },
    targetAudience: '酒店业主、总经理、运营总监、IT 负责人',
    contentPillars: ['收益管理', '数字化转型', '客户体验', 'OTA 运营', '会员体系'],
  },

  // ============================================
  // 5. 国学
  // ============================================
  {
    tag: '国学',
    name: '国学智慧',
    description: '国学智慧与现代应用',
    positioning: '面向企业管理者与文化爱好者，将国学智慧与现代管理、生活结合',
    keywords: ['国学', '易经', '论语', '道德经', '中庸', '管理哲学', '东方智慧', '孙子兵法', '阳明心学', '禅修'],
    primaryPlatforms: ['xiaohongshu', 'wechat'],
    voice: {
      tone: '温和深邃',
      style: '古今对照 + 思想提炼',
      forbiddenWords: ['迷信', '算命'],
      signatureWords: ['道', '德', '中庸', '无为', '知行合一'],
    },
    visual: {
      primaryColor: '#92400E',
      secondaryColor: '#78350F',
      fontFamily: '宋体',
      imageStyle: '褐色古典感，水墨画风格，古籍书页',
    },
    targetAudience: '企业管理者、文化爱好者、修身养性人群',
    contentPillars: ['经典解读', '管理智慧', '处世哲学', '修身养性', '古今对话'],
  },

  // ============================================
  // 6. Agent
  // ============================================
  {
    tag: 'Agent',
    name: 'AI Agent',
    description: 'AI Agent 技术与落地',
    positioning: '面向 Agent 开发者与技术决策者，分享 Agent 架构、工具调用、多 Agent 协作经验',
    keywords: ['Agent', '智能体', 'Multi-Agent', 'ReAct', 'Tool Use', 'AutoGPT', 'LangChain', 'LlamaIndex', 'MCP', 'Function Calling'],
    primaryPlatforms: ['zhihu', 'wechat', 'video_script'],
    voice: {
      tone: '前沿探索',
      style: '代码 + 架构图 + 实战案例',
      forbiddenWords: ['AI万能', '取代人类'],
      signatureWords: ['Agent', 'Tool', 'Plan', 'Reflect', 'Collaborate'],
    },
    visual: {
      primaryColor: '#DC2626',
      secondaryColor: '#B91C1C',
      fontFamily: '等宽字体',
      imageStyle: '红色活力感，Agent 协作流程图，工具调用拓扑图',
    },
    targetAudience: 'AI 工程师、Agent 开发者、技术决策者',
    contentPillars: ['Agent 架构', '工具调用', '多 Agent 协作', 'Agent 应用', 'Agent 框架'],
  },

  // ============================================
  // 7. 企业管理
  // ============================================
  {
    tag: '企业管理',
    name: '企业管理',
    description: '企业管理与组织效能',
    positioning: '面向企业高管与中层管理者，提供组织设计、流程优化、数字化转型方法论',
    keywords: ['企业管理', 'OKR', '组织架构', '流程优化', '数字化转型', '精益管理', '绩效', '人才发展', '变革管理', '战略'],
    primaryPlatforms: ['wechat', 'zhihu'],
    voice: {
      tone: '权威专业',
      style: '方法论 + 案例分析',
      forbiddenWords: ['画大饼', 'PUA'],
      signatureWords: ['OKR', '组织能力', '流程', '效能', '战略'],
    },
    visual: {
      primaryColor: '#0EA5E9',
      secondaryColor: '#0284C7',
      fontFamily: '无衬线',
      imageStyle: '蓝色专业感，组织架构图，流程图，仪表盘',
    },
    targetAudience: 'CEO、COO、HR 负责人、中层管理者',
    contentPillars: ['组织设计', '流程优化', '数字化转型', '绩效管理', '战略规划'],
  },

  // ============================================
  // 8. 产品
  // ============================================
  {
    tag: '产品',
    name: '产品思维',
    description: '产品设计与产品管理',
    positioning: '面向产品经理与设计师，分享需求分析、用户体验、产品策略与增长方法',
    keywords: ['产品', '产品经理', 'PRD', '用户体验', 'MVP', '需求分析', '产品策略', '增长', 'A/B测试', '用户研究'],
    primaryPlatforms: ['xiaohongshu', 'wechat'],
    voice: {
      tone: '敏锐洞察',
      style: '用户视角 + 数据验证',
      forbiddenWords: ['我觉得', '可能'],
      signatureWords: ['用户场景', '需求', 'MVP', '北极星', '留存'],
    },
    visual: {
      primaryColor: '#EC4899',
      secondaryColor: '#BE185D',
      fontFamily: '无衬线',
      imageStyle: '粉色创意感，用户旅程图，原型设计稿',
    },
    targetAudience: '产品经理、设计师、增长负责人',
    contentPillars: ['需求分析', '用户体验', '产品策略', '增长方法', '产品复盘'],
  },

  // ============================================
  // 9. XEAOS
  // ============================================
  {
    tag: 'XEAOS',
    name: 'XEAOS',
    description: 'XEAOS AI 企业操作系统',
    positioning: 'XEAOS 官方品牌，分享 AI 企业操作系统的设计理念、Runtime 架构、Hermes 能力与落地案例',
    keywords: ['XEAOS', 'AI操作系统', '企业大脑', 'Hermes', 'Runtime', 'Workflow', '知识图谱', 'Business OS', '数字孪生', 'Multi-Agent'],
    primaryPlatforms: ['wechat', 'video_script', 'live_script'],
    voice: {
      tone: '宏大远见',
      style: '系统化 + 生态视角',
      forbiddenWords: ['竞品对比', '负面评价'],
      signatureWords: ['XEAOS', 'Runtime', 'Hermes', '企业大脑', 'Business OS'],
    },
    visual: {
      primaryColor: '#0B1220',
      secondaryColor: '#1A2332',
      fontFamily: '无衬线',
      imageStyle: '深色科技感，霓虹蓝绿色光晕，玻璃态卡片，企业架构图',
    },
    targetAudience: '企业决策者、CTO、AI 战略负责人、合作伙伴',
    contentPillars: ['产品理念', 'Runtime 架构', 'Hermes 能力', '落地案例', '生态合作'],
  },
];

// ============================================
// 默认配置
// ============================================

export default {
  /** 品牌库版本 */
  version: '1.0.0',
  /** 品牌库描述 */
  description: 'XEAOS 品牌库 - 9 大品牌完整画像',
  /** 一致性检查通过阈值（>=该值才算 passed） */
  consistencyPassThreshold: 70,
  /** 分类置信度阈值（>=该值才返回主品牌） */
  classificationConfidenceThreshold: 40,
  /** 品牌内容记录保留天数 */
  contentRecordRetentionDays: 90,
  /** 品牌库 */
  brandLibrary: BRAND_LIBRARY,
};
